import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import DOMPurify from 'dompurify';
import { useAuth } from '../contexts/AuthContext';
import { DatePicker } from '../components/DateTimePicker';
import { formatDateShort, formatDateTime, formatDateLong } from '../utils/dateUtils';
import {
  getQuote, updateQuote, updateQuoteStatus, deleteQuote, reviseQuote,
  createQuoteDescription, updateQuoteDescription, deleteQuoteDescription,
  createQuoteItem, updateQuoteItem, deleteQuoteItem,
  createJob, createInvoice, addDescriptionsToJob,
  getItemsServices, getWorkersDropdown,
  getQuoteDescriptionSuggestions, getQuoteDescriptionById,
  uploadAttachment, getAttachmentsByEntity, downloadAttachment, downloadThumbnail, deleteAttachment,
  createComment, scheduleSiteVisit, downloadQuotePDF,
  getAllUsersDropdown, assignQuote
} from '../services/api';
import {
  ArrowLeft, Loader2, Plus, Edit, Trash2, X, CheckCircle,
  Briefcase, Receipt, RefreshCw, XCircle, Calendar, Upload, Download,
  Paperclip, MessageCircle, Send, Mail, User, AlertCircle, Eye, Link2, Camera
} from 'lucide-react';
import { RichTextEditor } from '../components/RichTextEditor';

import { formatDescriptionHtml, statusConfig, resizeImageFile } from '../components/quotes/QuoteHelpers';
import { canSeePrices } from '../utils/permissions';
import { AttachmentsModal } from '../components/quotes/AttachmentsModal';
import { CostBuilderModal } from '../components/quotes/CostBuilderModal';
import { DescriptionModal } from '../components/quotes/DescriptionModal';
import { EmailModal } from '../components/quotes/EmailModal';
import { DepositInvoiceModal } from '../components/quotes/DepositInvoiceModal';
import { SiteVisitModal } from '../components/quotes/SiteVisitModal';
import { ConfirmModal } from '../components/quotes/ConfirmModal';
import { ReviseModal } from '../components/quotes/ReviseModal';
import { CreateJobModal } from '../components/quotes/CreateJobModal';
import { CommentsSection } from '../components/quotes/CommentsSection';
import { usePageTitle } from '../hooks/usePageTitle';
import { NotesSection, TermsOfTrade } from '../components/quotes/NotesSection';

function QuoteDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user: currentUser } = useAuth();
  const showPrices = canSeePrices(currentUser);
  
  const [showDescModal, setShowDescModal] = useState(false);
  const [editDesc, setEditDesc] = useState(null);
  const [showCostBuilder, setShowCostBuilder] = useState(false);
  const [activeDescId, setActiveDescId] = useState(null);
  const [showAttachments, setShowAttachments] = useState(false);
  const [showSiteVisit, setShowSiteVisit] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showDepositInvoice, setShowDepositInvoice] = useState(false);
  const [confirmModal, setConfirmModal] = useState({ open: false });
  const [expandedDesc, setExpandedDesc] = useState(null);
  const [showCreateJob, setShowCreateJob] = useState(false);
  const [showReviseModal, setShowReviseModal] = useState(false);
  const [editingDate, setEditingDate] = useState(null); // 'date' or 'expiry'
  const [dateValue, setDateValue] = useState('');

  const { data: quote, isLoading, refetch } = useQuery({
    queryKey: ['quote', id],
    queryFn: () => getQuote(id).then(res => res.data),
  });

  const { data: staffUsers } = useQuery({
    queryKey: ['usersDropdownAll'],
    queryFn: () => getAllUsersDropdown().then(res => res.data),
  });

  const clientCityRegionPostalLine = useMemo(
    () => [
      quote?.client?.po_city,
      quote?.client?.po_region,
      quote?.client?.po_postal_code,
    ].filter(Boolean).join(' '),
    [quote]
  );

  const assignMutation = useMutation({
    mutationFn: (userId) => assignQuote(id, { assigned_to_user_id: userId }),
    onSuccess: (res) => { refetch(); queryClient.invalidateQueries({ queryKey: ['quotes'] }); toast.success(res.data.message); },
    onError: () => toast.error('Failed to assign quote')
  });

  // Fetch attachment counts per description
  const descriptionIds = quote?.descriptions?.map(d => d.id) || [];
  const { data: attachmentCounts = {} } = useQuery({
    queryKey: ['attachmentCounts', id, descriptionIds.join(',')],
    queryFn: async () => {
      const counts = {};
      for (const descId of descriptionIds) {
        try {
          const res = await getAttachmentsByEntity('quote_description', descId);
          counts[descId] = (res.data || []).length;
        } catch { counts[descId] = 0; }
      }
      return counts;
    },
    enabled: descriptionIds.length > 0,
  });

  // Mutations
  const statusMutation = useMutation({
    mutationFn: (data) => updateQuoteStatus(id, typeof data === 'object' ? data : { status: data }),
    onSuccess: () => { refetch(); toast.success('Status updated'); setConfirmModal({ open: false }); }
  });

  usePageTitle(quote?.quote_number);

  const addDescMutation = useMutation({
    mutationFn: (data) => createQuoteDescription({ ...data, quote_id: id }),
    onSuccess: () => { refetch(); setShowDescModal(false); toast.success('Description added'); }
  });

  const updateDescMutation = useMutation({
    mutationFn: ({ descId, data }) => updateQuoteDescription(descId, { ...data, quote_id: id }),
    onSuccess: () => { refetch(); setShowDescModal(false); setEditDesc(null); toast.success('Description updated'); }
  });

  const deleteDescMutation = useMutation({
    mutationFn: deleteQuoteDescription,
    onSuccess: () => { refetch(); toast.success('Line removed'); }
  });

  const addItemMutation = useMutation({
    mutationFn: createQuoteItem,
    onSuccess: () => { refetch(); toast.success('Item added'); }
  });

  const deleteItemMutation = useMutation({
    mutationFn: deleteQuoteItem,
    onSuccess: () => { refetch(); toast.success('Item removed'); }
  });

  const updateItemMutation = useMutation({
    mutationFn: ({ id, data }) => updateQuoteItem(id, data),
    onSuccess: () => { refetch(); }
  });

  const createJobMutation = useMutation({
    mutationFn: createJob,
    onSuccess: (res) => { 
      refetch(); 
      toast.success(`Job ${res.data.job_number} created`);
      navigate(`/jobs/${res.data.job_id || res.data.id}`);
    }
  });

  const addToJobMutation = useMutation({
    mutationFn: ({ jobId, data }) => addDescriptionsToJob(jobId, data),
    onSuccess: (res) => {
      refetch();
      setShowCreateJob(false);
      toast.success('Descriptions added to existing job');
      navigate(`/jobs/${res.data.job_id}`);
    },
    onError: () => toast.error('Failed to add descriptions to job')
  });

  const createInvoiceMutation = useMutation({
    mutationFn: createInvoice,
    onSuccess: (res) => {
      refetch();
      setShowDepositInvoice(false);
      toast.success(`Invoice ${res.data.invoice_number} created`);
      navigate(`/invoices/${res.data.invoice_id || res.data.id}`);
    }
  });

  const reviseMutation = useMutation({
    mutationFn: (remarks) => reviseQuote(id, { remarks }),
    onSuccess: (res) => {
      setShowReviseModal(false);
      toast.success(`Quote revised. New quote: ${res.data.quote_number}`);
      navigate(`/quotes/${res.data.quote_id}`);
    },
    onError: () => toast.error('Failed to revise quote')
  });

  const siteVisitMutation = useMutation({
    mutationFn: (data) => scheduleSiteVisit(id, data),
    onSuccess: () => { refetch(); setShowSiteVisit(false); toast.success('Site visit scheduled'); },
    onError: () => toast.error('Failed to schedule site visit')
  });

  const sendEmailMutation = useMutation({
    mutationFn: (data) => import('../services/api').then(({ sendQuoteEmail }) => sendQuoteEmail(id, data)),
    onSuccess: (res) => {
      refetch();
      setShowEmailModal(false);
      const token = res?.data?.acceptance_token;
      if (res?.data?.pending) {
        toast.info(res.data.message, { duration: 10000 });
      } else if (token) {
        const link = `${window.location.origin}/quote/acceptance/${token}`;
        navigator.clipboard.writeText(link).catch(() => {});
        toast.success('Quote email sent! Acceptance link copied to clipboard.');
      } else {
        toast.success('Quote email sent successfully');
      }
    },
    onError: (err) => toast.error(err?.response?.data?.detail || 'Failed to send email')
  });

  // Calculations — memoised so totals don't re-compute on every unrelated re-render.
  const { subtotal, gst, grandTotal } = useMemo(() => {
    const sub = quote?.descriptions?.reduce((sum, desc) =>
      sum + (desc.items?.reduce((s, i) => s + (i.total_cost || 0), 0) || desc.total_cost || 0), 0) || 0;
    const g = sub * 0.1;
    return { subtotal: sub, gst: g, grandTotal: sub + g };
  }, [quote?.descriptions]);
  
  // Lock editing when quote is Accepted or beyond (status >= 4)
  const isLocked = quote?.status >= 4;

  const handleStatusChange = (newStatus) => {
    const statusMessages = {
      2: { title: 'Change quote status confirmation', message: 'Are you sure you want to change quote status to FOR SUBMISSION?' },
      1: { title: 'Assign Site Visit', message: 'This will assign the quote for site visit.' },
      4: { title: 'Accept Quote Manually', message: 'Are you sure you want to ACCEPT this quote manually?' },
      '-1': { title: 'Cancel Quote', message: 'Are you sure you want to CANCEL this quote?', variant: 'danger', needsReason: true },
      '-2': { title: 'Decline Quote', message: 'Are you sure you want to mark this quote as DECLINED?', variant: 'danger', needsReason: true },
    };
    setConfirmModal({ open: true, status: newStatus, remarks: '', ...statusMessages[newStatus] });
  };

  const handleDescSubmit = (data) => {
    if (editDesc) {
      updateDescMutation.mutate({ descId: editDesc.id, data });
    } else {
      addDescMutation.mutate(data);
    }
  };

  const handleCreateJob = (formData) => {
    createJobMutation.mutate({
      quote_id: quote.id,
      client_id: quote.client_id,
      property_address_id: quote.property_address_id,
      is_to_book: !!formData.is_to_book,
      date_start: formData.is_to_book ? null : formData.date_start,
      date_end: formData.is_to_book ? null : formData.date_end,
      remarks: formData.remarks,
      quote_description_ids: formData.selectedDescIds || quote.descriptions?.map(d => d.id) || [],
      assignees: (!formData.is_to_book && formData.assignee_id) ? [{ 
        assignee_id: formData.assignee_id, 
        start_time: formData.start_time || '08:00', 
        end_time: formData.end_time || '17:00' 
      }] : []
    });
  };

  const handleAddToJob = (data) => {
    addToJobMutation.mutate({ jobId: data.job_id, data });
  };

  const handleDownloadPDF = async () => {
    try {
      toast.info('Generating PDF...');
      const res = await downloadQuotePDF(id);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = `Quote_${quote?.quote_number || id}.pdf`;
      document.body.appendChild(a);
      a.click();
      // Delay cleanup to ensure browser processes the download
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }, 1000);
      toast.success('PDF downloaded');
    } catch (err) {
      console.error('PDF download error:', err);
      toast.error('Failed to generate PDF');
    }
  };

  const formatCurrency = (num) => {
    if (!num) return '0.00';
    return new Intl.NumberFormat('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(num);
  };

  const formatDate = (dateStr) => {
    return formatDateShort(dateStr);
  };

  if (isLoading) {
    return <div className="flex items-center justify-center py-12"><Loader2 size={32} className="animate-spin text-primary" /></div>;
  }

  const status = statusConfig[quote?.status] || statusConfig['0'];

  return (
    <div className="space-y-4 animate-fadeIn" data-testid="quote-detail-full">
      {/* Header Row */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Link to="/quotes" className="text-slate-600 hover:text-slate-800"><ArrowLeft size={20} /></Link>
          <span className="text-slate-600">Quote No:</span>
          <span className="text-2xl font-bold text-primary">{quote?.quote_number}</span>
        </div>

        {/* Action Buttons - Change based on status */}
        <div className="flex flex-wrap gap-2">
          <button onClick={() => navigate('/quotes')} className="px-3 py-1.5 text-sm border border-slate-300 rounded hover:bg-slate-50">Cancel</button>
          {showPrices && (
            <button onClick={handleDownloadPDF} data-testid="download-pdf-btn" className="px-3 py-1.5 text-sm bg-red-500 text-white rounded hover:bg-red-600 flex items-center gap-1">
              <Download size={14} />Download as PDF
            </button>
          )}
          
          {/* Draft status - Show For Submission & Assign Site Visit */}
          {quote?.status === 0 && (
            <>
              <button onClick={() => handleStatusChange(2)} className="px-3 py-1.5 text-sm bg-blue-500 text-white rounded hover:bg-blue-600">For Submission</button>
              <button onClick={() => setShowSiteVisit(true)} className="px-3 py-1.5 text-sm bg-green-500 text-white rounded hover:bg-green-600">Assign Site Visit Schedule</button>
              <button onClick={() => { if (window.confirm('Are you sure you want to remove this quote?')) { deleteQuote(id).then(() => { toast.success('Quote removed'); navigate('/quotes'); }).catch(() => toast.error('Failed to remove')); }}} className="px-3 py-1.5 text-sm bg-red-600 text-white rounded hover:bg-red-700" data-testid="remove-quote-btn">Remove</button>
            </>
          )}

          {/* Assigned status */}
          {quote?.status === 1 && (
            <>
              <button onClick={() => handleStatusChange(2)} className="px-3 py-1.5 text-sm bg-blue-500 text-white rounded hover:bg-blue-600">For Submission</button>
            </>
          )}
          
          {/* For Submission status - Show Accept Manually, Revise & Email */}
          {quote?.status === 2 && (
            <>
              <button onClick={() => handleStatusChange(4)} className="px-3 py-1.5 text-sm bg-blue-500 text-white rounded hover:bg-blue-600">Accept Manually</button>
              {showPrices && (
                <button onClick={() => setShowEmailModal(true)} className="px-3 py-1.5 text-sm bg-cyan-500 text-white rounded hover:bg-cyan-600 flex items-center gap-1" data-testid="email-quote-btn">
                  <Mail size={14} />Email
                </button>
              )}
              <button onClick={() => setShowReviseModal(true)} className="px-3 py-1.5 text-sm bg-green-500 text-white rounded hover:bg-green-600 flex items-center gap-1" data-testid="revise-quote-btn"><RefreshCw size={14} />Revise</button>
            </>
          )}

          {/* For Acceptance status */}
          {quote?.status === 3 && (
            <>
              <button onClick={() => handleStatusChange(4)} className="px-3 py-1.5 text-sm bg-blue-500 text-white rounded hover:bg-blue-600">Accept Manually</button>
              {showPrices && (
                <>
                  <button onClick={() => {
                    import('../services/api').then(({ getQuoteAcceptanceLink }) => {
                      getQuoteAcceptanceLink(quote.id).then(res => {
                        const link = `${window.location.origin}/quote/acceptance/${res.data.acceptance_token}`;
                        navigator.clipboard.writeText(link);
                        toast.success('Acceptance link copied to clipboard');
                      }).catch(() => toast.error('Failed to get acceptance link'));
                    });
                  }} className="px-3 py-1.5 text-sm bg-teal-500 text-white rounded hover:bg-teal-600 flex items-center gap-1" data-testid="copy-acceptance-link-btn">
                    <Link2 size={14} />Copy Acceptance Link
                  </button>
                  <button onClick={() => setShowEmailModal(true)} className="px-3 py-1.5 text-sm bg-cyan-500 text-white rounded hover:bg-cyan-600 flex items-center gap-1" data-testid="email-quote-btn-alt">
                    <Mail size={14} />Email
                  </button>
                </>
              )}
              <button onClick={() => setShowReviseModal(true)} className="px-3 py-1.5 text-sm bg-green-500 text-white rounded hover:bg-green-600 flex items-center gap-1" data-testid="revise-quote-btn"><RefreshCw size={14} />Revise</button>
            </>
          )}
          
          {/* Accepted status - Show Create Job, Create Deposit Invoice, Revise, Mark as Declined */}
          {quote?.status === 4 && (
            <>
              <button onClick={() => setShowCreateJob(true)} className="px-3 py-1.5 text-sm bg-blue-500 text-white rounded hover:bg-blue-600 flex items-center gap-1">
                <Briefcase size={14} />Create Job
              </button>
              {showPrices && (
                <button onClick={() => setShowDepositInvoice(true)} className="px-3 py-1.5 text-sm bg-cyan-500 text-white rounded hover:bg-cyan-600 flex items-center gap-1" data-testid="create-deposit-invoice-btn-accepted">
                  <Receipt size={14} />Create Deposit Invoice
                </button>
              )}
              <button onClick={() => setShowReviseModal(true)} className="px-3 py-1.5 text-sm bg-green-500 text-white rounded hover:bg-green-600 flex items-center gap-1" data-testid="revise-quote-btn"><RefreshCw size={14} />Revise</button>
              <button onClick={() => handleStatusChange(-2)} className="px-3 py-1.5 text-sm bg-yellow-500 text-white rounded hover:bg-yellow-600">Mark as Declined</button>
            </>
          )}

          {/* Job Booked or Invoiced status */}
          {(quote?.status === 5 || quote?.status === 6) && (
            <>
              {quote?.has_unbooked_descriptions && (
                <button onClick={() => setShowCreateJob(true)} className="px-3 py-1.5 text-sm bg-blue-500 text-white rounded hover:bg-blue-600 flex items-center gap-1">
                  <Briefcase size={14} />Create Job
                </button>
              )}
              {showPrices && (
                <button onClick={() => setShowDepositInvoice(true)} className="px-3 py-1.5 text-sm bg-cyan-500 text-white rounded hover:bg-cyan-600 flex items-center gap-1" data-testid="create-deposit-invoice-btn">
                  <Receipt size={14} />Create Deposit Invoice
                </button>
              )}
              <button onClick={() => setShowReviseModal(true)} className="px-3 py-1.5 text-sm bg-green-500 text-white rounded hover:bg-green-600 flex items-center gap-1" data-testid="revise-quote-btn"><RefreshCw size={14} />Revise</button>
              <button onClick={() => handleStatusChange(-2)} className="px-3 py-1.5 text-sm bg-yellow-500 text-white rounded hover:bg-yellow-600">Mark as Declined</button>
            </>
          )}

          {/* Cancel button - available for active statuses (0-3) */}
          {quote?.status >= 0 && quote?.status <= 3 && (
            <button onClick={() => handleStatusChange(-1)} className="px-3 py-1.5 text-sm bg-red-500 text-white rounded hover:bg-red-600" data-testid="cancel-quote-btn">Cancel Quote</button>
          )}
        </div>
      </div>

      {/* Created By / Assigned To Bar */}
      <div className="bg-white border border-slate-200 rounded-lg p-3 flex flex-wrap items-center gap-x-6 gap-y-2" data-testid="quote-accountability-bar">
        <div className="flex items-center gap-2 text-sm">
          <User size={14} className="text-slate-400" />
          <span className="text-slate-500">Created by:</span>
          <span className="font-medium text-slate-800" data-testid="quote-created-by">{quote?.added_by_name || '-'}</span>
        </div>
        <div className="flex items-center gap-2 text-sm">
          <User size={14} className="text-primary" />
          <span className="text-slate-500">Assigned to:</span>
          {(currentUser?.id === quote?.added_by_user_id || currentUser?.user_type === 0 || currentUser?.user_type === 5) ? (
            <>
              <select
                value={quote?.assigned_to_user_id || ''}
                onChange={(e) => { if (e.target.value) assignMutation.mutate(e.target.value); }}
                className="px-2 py-1 border border-slate-300 rounded text-sm font-medium min-w-[150px] focus:ring-1 focus:ring-primary focus:border-primary"
                data-testid="quote-assign-select"
              >
                <option value="">-- Not Assigned --</option>
                {staffUsers?.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
              </select>
              {assignMutation.isPending && <Loader2 size={14} className="animate-spin text-primary" />}
            </>
          ) : (
            <span className="font-medium text-slate-800" data-testid="quote-assigned-to-name">{quote?.assigned_to_name || 'Not Assigned'}</span>
          )}
        </div>
      </div>

      {/* Assignment Bar - Only show when Assigned (status 1) */}
      {quote?.status === 1 && quote?.site_visit && (
        <div className="flex items-center gap-4 border rounded-lg overflow-hidden" data-testid="assignment-bar">
          <div className="bg-amber-100 text-amber-700 px-5 py-2.5 text-sm font-medium shrink-0">Assigned</div>
          <div className="flex items-center gap-2 py-2 text-sm flex-1">
            <span className="font-bold text-primary">{quote.site_visit.assignee_name?.toUpperCase()}</span>
            <span className="text-slate-600">
              Site visit: {quote.site_visit.date ? (() => {
                return formatDateLong(quote.site_visit.date + 'T00:00:00');
              })() : ''} {quote.site_visit.time_start || ''}
            </span>
          </div>
          <button
            onClick={() => setShowSiteVisit(true)}
            className="px-5 py-2.5 bg-slate-200 text-slate-700 text-sm hover:bg-slate-300 shrink-0"
            data-testid="reschedule-btn"
          >
            Re-Schedule
          </button>
        </div>
      )}

      {/* Cancel Remarks (if exists) */}
      {quote?.cancel_remarks && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <h4 className="font-semibold text-red-800 mb-1">{quote.status === -2 ? 'Decline Reason' : 'Cancel Reason'}</h4>
          <p className="text-red-700 text-sm">{quote.cancel_remarks}</p>
        </div>
      )}

      {/* Revision Remarks (if exists) */}
      {quote?.revision_remarks && (
        <div className="bg-cyan-50 border border-cyan-200 rounded-lg p-4">
          <h4 className="font-semibold text-cyan-800 mb-1">Revision Remarks</h4>
          <p className="text-cyan-700 text-sm">{quote.revision_remarks}</p>
        </div>
      )}

      {/* Linked Jobs */}
      {quote?.jobs?.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4" data-testid="linked-jobs-banner">
          <div className="flex items-center gap-2 flex-wrap">
            <Briefcase size={16} className="text-blue-700" />
            <span className="font-semibold text-blue-800 text-sm">Linked Job{quote.jobs.length > 1 ? 's' : ''}:</span>
            {quote.jobs.map(j => (
              <Link key={j.id} to={`/jobs/${j.id}`} className="inline-flex items-center gap-1 px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 transition-colors" data-testid={`linked-job-${j.job_number}`}>
                {j.job_number}
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Linked Deposit Invoices */}
      {quote?.deposit_invoices?.length > 0 && (
        <div className="bg-cyan-50 border border-cyan-200 rounded-lg p-4" data-testid="linked-deposit-invoices-banner">
          <div className="flex items-center gap-2 flex-wrap">
            <Receipt size={16} className="text-cyan-700" />
            <span className="font-semibold text-cyan-800 text-sm">Deposit Invoice{quote.deposit_invoices.length > 1 ? 's' : ''}:</span>
            {quote.deposit_invoices.map(di => (
              <Link key={di.id} to={`/invoices/${di.id}`} className="inline-flex items-center gap-1 px-3 py-1 bg-cyan-600 text-white text-sm rounded hover:bg-cyan-700 transition-colors" data-testid={`linked-deposit-${di.invoice_number}`}>
                {di.invoice_number} ({di.deposit_percent}%)
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Quote Card */}
      <div className="flex justify-center">
        <div className="w-full max-w-5xl">
          <div className="bg-white border border-primary rounded-lg shadow-sm" style={{ minHeight: '60vh' }}>
            <div className="p-6">
              {/* Status Badge & Logo Row */}
              <div className="flex justify-between items-start mb-6">
                <div className="w-1/4">
                  <div className={`inline-block px-4 py-2 rounded text-sm font-medium ${status.color}`}>
                    {status.label}
                  </div>
                </div>
                <div className="w-1/3 text-right">
                  <img src="/logo.png" alt="Project Maintenance Perth" className="h-20 w-auto ml-auto" />
                </div>
              </div>

              {/* Quote Header Info */}
              <div className="grid grid-cols-3 gap-8 mb-6">
                <div>
                  <h2 className="text-2xl font-bold mb-2">QUOTE</h2>
                  <div className="text-sm text-slate-600 uppercase">
                    <div className="font-medium">{quote?.client_name}</div>
                    <div>{quote?.client?.po_address_line1}</div>
                    {quote?.client?.po_address_line2 && <div>{quote?.client?.po_address_line2}</div>}
                    <div>{clientCityRegionPostalLine}</div>
                    <div>{quote?.client?.po_country || 'AUSTRALIA'}</div>
                  </div>
                </div>
                <div className="text-sm">
                  <div className="mb-3"><div className="font-bold">Date</div>
                    {editingDate === 'date' ? (
                      <span className="flex items-center gap-1"><DatePicker value={dateValue} onChange={v => setDateValue(v)} />
                        <button onClick={() => { updateQuote(id, { created_at: dateValue }).then(() => { refetch(); setEditingDate(null); }); }} className="text-green-600 text-xs font-medium">Save</button>
                        <button onClick={() => setEditingDate(null)} className="text-red-500 text-xs">Cancel</button></span>
                    ) : (
                      <span className="text-slate-600">{formatDate(quote?.created_at)} {!isLocked && <Edit size={12} className="inline text-primary cursor-pointer ml-1" onClick={() => { setEditingDate('date'); setDateValue(quote?.created_at?.slice(0, 10) || ''); }} />}</span>
                    )}
                  </div>
                  <div className="mb-3"><div className="font-bold">Expiry</div>
                    {editingDate === 'expiry' ? (
                      <span className="flex items-center gap-1"><DatePicker value={dateValue} onChange={v => setDateValue(v)} />
                        <button onClick={() => { updateQuote(id, { expiry_date: dateValue }).then(() => { refetch(); setEditingDate(null); }); }} className="text-green-600 text-xs font-medium">Save</button>
                        <button onClick={() => setEditingDate(null)} className="text-red-500 text-xs">Cancel</button></span>
                    ) : (
                      <span className="text-slate-600">{formatDate(quote?.expiry_date)} {!isLocked && <Edit size={12} className="inline text-primary cursor-pointer ml-1" onClick={() => { setEditingDate('expiry'); setDateValue(quote?.expiry_date?.slice(0, 10) || ''); }} />}</span>
                    )}
                  </div>
                  <div className="mb-3"><div className="font-bold">Quote Number</div><span className="text-slate-600">{quote?.quote_number}</span></div>
                  <div><div className="font-bold">Reference</div><span className="text-slate-600">{quote?.property_address ? <a href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(quote.property_address)}`} target="_blank" rel="noopener noreferrer" className="text-[#3498db] hover:underline" data-testid="address-map-link">{quote.property_address}</a> : '-'}</span></div>
                </div>
                <div className="text-sm text-right">
                  <div>Phone: 08 6204 8116</div>
                  <div className="mb-4">Mobile: 0412 627 697</div>
                  <div>ABN: 69 516 939 208</div>
                  <div className="text-primary mb-4">info@projectmaintenanceperth.com</div>
                  <div className="font-bold">Registered Painting Contractor: 100457</div>
                </div>
              </div>

              <hr className="my-4" />

              {/* Description Table */}
              <table className="w-full text-sm table-fixed">
                <thead>
                  <tr className="border-b-2">
                    <th className="p-2 text-left font-bold">Description</th>
                    {showPrices && <th className="p-2 text-right font-bold" style={{ width: '110px' }}>Amount</th>}
                  </tr>
                </thead>
                <tbody>
                  {quote?.descriptions?.length === 0 ? (
                    <tr>
                      <td colSpan={showPrices ? 2 : 1} className="p-8 text-center text-slate-400">No items added yet. Click "Add New Line" to start.</td>
                    </tr>
                  ) : (
                    quote?.descriptions?.map((desc) => {
                      const descTotal = desc.items?.reduce((s, i) => s + (i.total_cost || 0), 0) || desc.total_cost || 0;
                      const isExpanded = expandedDesc === desc.id;

                      return (
                        <tr key={desc.id} className="border-t" id={`description-${desc.id}`}>
                          <td className="p-3" style={{ maxWidth: 0, width: '100%' }}>
                            <div className="cursor-pointer" onClick={() => setExpandedDesc(isExpanded ? null : desc.id)}>
                              <h6 className="font-bold text-slate-800">{desc.job_title}</h6>
                              <div className="text-slate-600 mt-2 prose prose-sm max-w-none overflow-hidden break-words [word-break:break-word]" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(formatDescriptionHtml(desc.description)) }} />
                            </div>
                            
                            {isExpanded && (
                              <div className="flex gap-2 mt-4">
                                <button onClick={() => { setActiveDescId(desc.id); setShowAttachments(true); }} className="px-3 py-1.5 text-xs bg-slate-500 text-white rounded hover:bg-slate-600 flex items-center gap-1.5">
                                  Attachments
                                  {(attachmentCounts[desc.id] > 0) && <span className="bg-white text-slate-700 font-bold rounded-full w-5 h-5 flex items-center justify-center text-[10px]">{attachmentCounts[desc.id]}</span>}
                                </button>
                                {!isLocked && (
                                  <>
                                    <button onClick={() => { setActiveDescId(desc.id); setShowCostBuilder(true); }} className="px-3 py-1.5 text-xs bg-yellow-500 text-white rounded hover:bg-yellow-600">Add/Remove Items</button>
                                    <button onClick={() => { setEditDesc(desc); setShowDescModal(true); }} className="px-3 py-1.5 text-xs bg-blue-500 text-white rounded hover:bg-blue-600">Edit Description</button>
                                    <button onClick={() => { if(window.confirm('Remove this line?')) deleteDescMutation.mutate(desc.id); }} className="px-3 py-1.5 text-xs bg-red-500 text-white rounded hover:bg-red-600">Remove this line</button>
                                  </>
                                )}
                              </div>
                            )}
                          </td>
                          <td className="p-3 text-right align-top" style={{ display: showPrices ? undefined : 'none' }}>
                            <span className="text-primary font-bold cursor-pointer hover:underline" onClick={() => { setActiveDescId(desc.id); setShowCostBuilder(true); }}>
                              {formatCurrency(descTotal)}
                            </span>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>

              {/* NOTES Section */}
              <NotesSection quote={quote} quoteId={id} refetch={refetch} isLocked={isLocked} />

              {/* Totals - hidden from Ops users */}
              {showPrices && quote?.descriptions?.length > 0 && (
                <div className="mt-4">
                  <table className="w-full text-sm">
                    <tbody>
                      <tr className="border-t-2"><td></td><td className="text-right py-2 w-28">Subtotal</td><td className="text-right py-2 w-32">{formatCurrency(subtotal)}</td></tr>
                      <tr><td></td><td className="text-right py-2 border-b">Total GST 10%</td><td className="text-right py-2 border-b">{formatCurrency(gst)}</td></tr>
                      <tr className="font-bold"><td></td><td className="text-right py-2 border-b">TOTAL AUD</td><td className="text-right py-2 border-b">{formatCurrency(grandTotal)}</td></tr>
                    </tbody>
                  </table>
                </div>
              )}

              {/* Add New Line Button - hidden when locked */}
              {!isLocked && (
                <div className="mt-6 text-center">
                  <button onClick={() => { setEditDesc(null); setShowDescModal(true); }} className="px-8 py-2 bg-primary text-white rounded hover:bg-primary-hover" data-testid="add-description-btn">
                    <Plus size={16} className="inline mr-2" />Add New Line
                  </button>
                </div>
              )}

              {/* Terms of Trade - visible in print, collapsible on screen */}
              <TermsOfTrade />
            </div>
          </div>

          {/* Comments Section - Internal Only */}
          <div className="mt-6">
            <CommentsSection 
              comments={quote?.comments || []} 
              onAddComment={async (comment) => {
                try {
                  await createComment({ entity_type: 'quote', entity_id: id, comment });
                  refetch();
                  toast.success('Comment added');
                } catch { toast.error('Failed to add comment'); }
              }}
              onCommentsChanged={() => refetch()}
            />
          </div>
        </div>
      </div>

      {/* Modals */}
      <AttachmentsModal isOpen={showAttachments} onClose={() => setShowAttachments(false)} descriptionId={activeDescId} />
      <CostBuilderModal isOpen={showCostBuilder} onClose={() => setShowCostBuilder(false)} descriptionId={activeDescId} quoteId={id} existingItems={quote?.descriptions?.find(d => d.id === activeDescId)?.items || []} onAddItem={(data) => addItemMutation.mutate(data)} onRemoveItem={(itemId) => deleteItemMutation.mutate(itemId)} onUpdateItem={(itemId, data) => updateItemMutation.mutate({ id: itemId, data })} refetch={refetch} readOnly={isLocked} />
      <DescriptionModal key={(editDesc?.id || 'new') + '-' + showDescModal} isOpen={showDescModal} onClose={() => { setShowDescModal(false); setEditDesc(null); }} onSubmit={handleDescSubmit} loading={addDescMutation.isPending || updateDescMutation.isPending} editData={editDesc} />
      <SiteVisitModal isOpen={showSiteVisit} onClose={() => setShowSiteVisit(false)} onSubmit={(data) => siteVisitMutation.mutate(data)} loading={siteVisitMutation.isPending} quote={quote} />
      <EmailModal isOpen={showEmailModal} onClose={() => setShowEmailModal(false)} quote={quote} onSend={(data) => sendEmailMutation.mutate(data)} loading={sendEmailMutation.isPending} />
      {showDepositInvoice && <DepositInvoiceModal onClose={() => setShowDepositInvoice(false)} quote={quote} grandTotal={subtotal} onSubmit={(data) => createInvoiceMutation.mutate(data)} loading={createInvoiceMutation.isPending} />}
      <ConfirmModal isOpen={confirmModal.open} onClose={() => setConfirmModal({ open: false })} onConfirm={() => { 
        const payload = confirmModal.needsReason
          ? { status: confirmModal.status, cancel_remarks: [confirmModal.reason, confirmModal.remarks].filter(Boolean).join(' - ') }
          : confirmModal.status;
        statusMutation.mutate(payload);
      }} title={confirmModal.title} message={confirmModal.message} confirmText="Confirm" loading={statusMutation.isPending} variant={confirmModal.variant}>
        {confirmModal.needsReason && (
          <div className="mt-3 space-y-3">
            <div>
              <label className="block text-sm font-medium mb-1">Reason *</label>
              <select
                value={confirmModal.reason || ''}
                onChange={e => setConfirmModal(prev => ({ ...prev, reason: e.target.value }))}
                className="w-full px-3 py-2 border border-slate-300 rounded text-sm"
                data-testid="cancel-reason-select"
              >
                <option value="">Select a reason...</option>
                <option value="No response">No response</option>
                <option value="Pricing issue">Pricing issue</option>
                <option value="Lost to competitor">Lost to competitor</option>
                <option value="Project cancelled">Project cancelled</option>
                <option value="Project on hold / postponed">Project on hold / postponed</option>
                <option value="Scope changed">Scope changed</option>
                <option value="Insufficient capacity / timing">Insufficient capacity / timing</option>
                <option value="Funding / budget not approved">Funding / budget not approved</option>
                <option value="Client went in-house">Client went in-house</option>
                <option value="Other reason">Other reason</option>
              </select>
            </div>
            {confirmModal.reason === 'Other reason' && (
              <div>
                <label className="block text-sm font-medium mb-1">Additional remarks</label>
                <textarea value={confirmModal.remarks || ''} onChange={e => setConfirmModal(prev => ({ ...prev, remarks: e.target.value }))} className="w-full px-3 py-2 border border-slate-300 rounded text-sm" rows={2} placeholder="Enter additional details..." data-testid="cancel-remarks-textarea" />
              </div>
            )}
          </div>
        )}
      </ConfirmModal>
      <CreateJobModal isOpen={showCreateJob} onClose={() => setShowCreateJob(false)} onSubmit={handleCreateJob} onAddToJob={handleAddToJob} loading={createJobMutation.isPending || addToJobMutation.isPending} descriptions={quote?.descriptions || []} existingJobs={quote?.existing_jobs || []} />
      <ReviseModal isOpen={showReviseModal} onClose={() => setShowReviseModal(false)} onSubmit={(remarks) => reviseMutation.mutate(remarks)} loading={reviseMutation.isPending} />
    </div>
  );
}

export default QuoteDetail;
